import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ITrack } from "@agp/shared.user-app/module/guide-player.module/audio-client";
import { MonotoneMinAudioPlayerHeight } from "../const/audio-player-layout";
import { useLanguageBasedFont } from "../const/font-utils";
import MonotoneMiniAudioPlayer from "./monotone-mini-audio-player";
import MonotoneFullAudioPlayer from "./monotone-full-audio-player";
import ServiceLocator from "../../../../domain/service-locator";

type MonotoneAudioPlayerProps = {
  track: ITrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onNext?: () => void;
  onPrev?: () => void;
  onClose?: () => void;
};

const MonotoneAudioPlayer: React.FC<MonotoneAudioPlayerProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onNext,
  onClose,
}) => {
  // const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [lastInteractionType, setLastInteractionType] = useState<
    "touch" | "mouse" | null
  >(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const fontClass = useLanguageBasedFont();

  const layoutModule = ServiceLocator.PlayerLayoutModule;
  const playerType = layoutModule.playerType.watch();
  const isExpanded = useMemo(() => playerType === "full", [playerType]);

  // スワイプの進捗を計算（0-1の範囲）
  const getSwipeProgress = () => {
    const maxDrag = 200; // スワイプの最大距離
    const progress = Math.max(0, Math.min(1, dragOffset / maxDrag));
    return isExpanded ? 1 - progress : progress;
  };

  // 高さを動的に計算
  const getDynamicHeight = () => {
    const maxHeight = window.innerHeight - 120; // 拡張時の高さ
    const progress = getSwipeProgress();
    return (
      MonotoneMinAudioPlayerHeight +
      (maxHeight - MonotoneMinAudioPlayerHeight) * progress
    );
  };

  // 拡張時の内容の不透明度を計算（クロスフェード）
  const getContentOpacity = () => {
    const progress = getSwipeProgress();
    if (isDragging) {
      return progress; // 0から1までの直線的な変化
    }
    return isExpanded ? 1 : 0;
  };

  // 縮小時の内容の不透明度を計算（クロスフェード）
  const getCollapsedContentOpacity = () => {
    const progress = getSwipeProgress();
    if (isDragging) {
      return 1 - progress; // 1から0までの直線的な変化
    }
    return isExpanded ? 0 : 1;
  };
  // ドラッグハンドル専用のイベントハンドラー
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    // マウスイベントが直前に発生していた場合、タッチイベントを無視
    if (lastInteractionType === "mouse") return;

    e.stopPropagation();
    setLastInteractionType("touch");
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setDragOffset(0);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (!isDragging || lastInteractionType !== "touch") return;

    const currentY = e.touches[0].clientY;
    const offset = dragStartY - currentY;

    // 拡張時は下向きのドラッグを許可、縮小時は上向きのドラッグを許可
    if (isExpanded && offset < 0) {
      setDragOffset(Math.abs(offset));
    } else if (!isExpanded && offset > 0) {
      setDragOffset(offset);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();
    if (!isDragging || lastInteractionType !== "touch") return;

    setIsDragging(false);

    if (dragOffset < 10) {
      layoutModule.playerType.setValue(isExpanded ? "mini" : "full");
      setDragOffset(0);
      // インタラクションタイプをリセット
      setTimeout(() => setLastInteractionType(null), 100);
      return;
    }

    const threshold = 50;

    if (dragOffset > threshold) {
      if (!isExpanded) {
        layoutModule.playerType.setValue("full");
      } else {
        layoutModule.playerType.setValue("mini");
      }
    }

    setDragOffset(0);
    // インタラクションタイプをリセット
    setTimeout(() => setLastInteractionType(null), 100);
  };

  // 縮小表示用のスライド検知ハンドラー（操作可能な要素を除外）
  const handleCollapsedTouchStart = (e: React.TouchEvent) => {
    // マウスイベントが直前に発生していた場合、タッチイベントを無視
    if (lastInteractionType === "mouse") return;

    const target = e.target as HTMLElement;

    // 操作可能な要素（ボタン、プログレスバー）がタッチされた場合はスライド処理を無効化
    if (
      target.closest("button") ||
      target.closest("#monotone-progress-bar") ||
      target.closest('[data-progress-fill="true"]') ||
      target.closest('[data-progress-handle="true"]')
    ) {
      return;
    }

    e.stopPropagation();
    setLastInteractionType("touch");
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
    setDragOffset(0);
  };

  const handleCollapsedTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || lastInteractionType !== "touch") return;

    const target = e.target as HTMLElement;

    // 操作可能な要素上でのドラッグは無効化
    if (
      target.closest("button") ||
      target.closest("#monotone-progress-bar") ||
      target.closest('[data-progress-fill="true"]') ||
      target.closest('[data-progress-handle="true"]')
    ) {
      return;
    }

    e.stopPropagation();

    const currentY = e.touches[0].clientY;
    const offset = dragStartY - currentY;

    // 拡張時は下向きのドラッグを許可、縮小時は上向きのドラッグを許可
    if (isExpanded && offset < 0) {
      setDragOffset(Math.abs(offset));
    } else if (!isExpanded && offset > 0) {
      setDragOffset(offset);
    }
  };

  const handleCollapsedTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging || lastInteractionType !== "touch") return;

    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();

    setIsDragging(false);

    const threshold = 50;

    if (dragOffset > threshold) {
      if (!isExpanded) {
        layoutModule.playerType.setValue("full");
      } else {
        layoutModule.playerType.setValue("mini");
      }
    }

    setDragOffset(0);
    // インタラクションタイプをリセット
    setTimeout(() => setLastInteractionType(null), 100);
  };

  const handleDragHandleMouseDown = (e: React.MouseEvent) => {
    // タッチイベントが直前に発生していた場合、マウスイベントを無視
    if (lastInteractionType === "touch") return;

    e.preventDefault();
    e.stopPropagation();
    setLastInteractionType("mouse");
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragOffset(0);
  };

  // 縮小表示用のマウスハンドラー
  const handleCollapsedMouseDown = (e: React.MouseEvent) => {
    // タッチイベントが直前に発生していた場合、マウスイベントを無視
    if (lastInteractionType === "touch") return;

    const target = e.target as HTMLElement;

    // 操作可能な要素がクリックされた場合はスライド処理を無効化
    if (
      target.closest("button") ||
      target.closest("#monotone-progress-bar") ||
      target.closest('[data-progress-fill="true"]') ||
      target.closest('[data-progress-handle="true"]')
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setLastInteractionType("mouse");
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragOffset(0);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || lastInteractionType !== "mouse") return;

      const currentY = e.clientY;
      const offset = dragStartY - currentY;

      if (isExpanded && offset < 0) {
        setDragOffset(Math.abs(offset));
      } else if (!isExpanded && offset > 0) {
        setDragOffset(offset);
      }
    },
    [isDragging, lastInteractionType, dragStartY, isExpanded]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging || lastInteractionType !== "mouse") return;

    setIsDragging(false);

    // ドラッグ距離が小さい場合は単純なクリックとして扱い、状態を切り替え
    if (dragOffset < 10) {
      layoutModule.playerType.setValue(isExpanded ? "mini" : "full");
      setDragOffset(0);
      // インタラクションタイプをリセット
      setTimeout(() => setLastInteractionType(null), 100);
      return;
    }

    const threshold = 50;

    if (dragOffset > threshold) {
      if (!isExpanded) {
        layoutModule.playerType.setValue("full");
      } else {
        layoutModule.playerType.setValue("mini");
      }
    }

    setDragOffset(0);
    // インタラクションタイプをリセット
    setTimeout(() => setLastInteractionType(null), 100);
  }, [
    isDragging,
    lastInteractionType,
    dragOffset,
    layoutModule.playerType,
    isExpanded,
  ]);

  const handleClose = () => {
    onClose?.();
    setTimeout(() => layoutModule.playerType.setValue("mini"), 300);
  };

  useEffect(() => {
    if (isDragging && lastInteractionType === "mouse") {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    return;
  }, [isDragging, lastInteractionType, handleMouseMove, handleMouseUp]);

  const dynamicHeight = getDynamicHeight();
  const expandedContentOpacity = getContentOpacity();
  const collapsedContentOpacity = getCollapsedContentOpacity();

  return (
    <div
      ref={playerRef}
      className={`overflow-hidden absolute left-4 right-4 bg-white rounded-3xl shadow-[0_0_20px_rgba(0,0,0,0.3)] transition-all duration-300 ease-out ${fontClass}`}
      style={{
        bottom: "16px",
        height: isDragging
          ? `${dynamicHeight}px`
          : isExpanded
          ? `${window.innerHeight - 50}px`
          : "130px",
        opacity: track ? 1 : 0,
        pointerEvents: track ? "auto" : "none",
        visibility: track ? "visible" : "hidden",
      }}
    >
      {/* ドラッグハンドル */}
      <div
        className="flex justify-center pt-6 pb-1 relative z-10"
        style={{ minHeight: "24px" }}
        onTouchStart={handleDragHandleTouchStart}
        onTouchMove={handleDragHandleTouchMove}
        onTouchEnd={handleDragHandleTouchEnd}
        onMouseDown={handleDragHandleMouseDown}
      >
        <div className="w-20 h-[4px] bg-gray-300 rounded-full" />
      </div>

      {/* 拡張表示の内容 */}
      <MonotoneFullAudioPlayer
        track={track}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        onPlayPause={onPlayPause}
        onSeek={onSeek}
        onNext={onNext}
        onTouchStart={handleDragHandleTouchStart}
        onTouchMove={handleDragHandleTouchMove}
        onTouchEnd={handleDragHandleTouchEnd}
        onMouseDown={handleDragHandleMouseDown}
        opacity={expandedContentOpacity}
        pointerEvents={expandedContentOpacity > 0.5 ? "auto" : "none"}
        onClose={handleClose}
      />

      {/* 縮小表示の内容 */}
      <MonotoneMiniAudioPlayer
        track={track}
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        onPlayPause={onPlayPause}
        onSeek={onSeek}
        onNext={onNext}
        onTouchStart={handleCollapsedTouchStart}
        onTouchMove={handleCollapsedTouchMove}
        onTouchEnd={handleCollapsedTouchEnd}
        onMouseDown={handleCollapsedMouseDown}
        opacity={collapsedContentOpacity}
        pointerEvents={collapsedContentOpacity > 0.5 ? "auto" : "none"}
        onClose={handleClose}
      />
    </div>
  );
};

export default MonotoneAudioPlayer;
